import { inferAdditionalFields } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import { api } from "./api";
import { requestHelpAttention } from "./help-attention";
import { isNativeApp, PUBLIC_ORIGIN } from "./platform";
import { saveSessionToken, sessionToken } from "./session-token";

export const authClient = createAuthClient({
  baseURL: isNativeApp ? PUBLIC_ORIGIN : window.location.origin,
  basePath: "/api/auth",
  // Sign-up also takes these; the server checks them (see apps/api/src/auth.ts).
  plugins: [
    inferAdditionalFields({
      user: {
        phoneNumber: { type: "string", required: true },
        birthdate: { type: "string", required: true },
      },
    }),
  ],
  // The apps keep the token the server returns on sign-in (and on each session
  // refresh) and send it with every request, instead of a cookie.
  fetchOptions: isNativeApp
    ? {
        credentials: "omit",
        auth: { type: "Bearer", token: () => sessionToken() ?? undefined },
        onSuccess: async ({ response }) => {
          const token = response.headers.get("set-auth-token");
          if (token) await saveSessionToken(token);
        },
      }
    : undefined,
});

/** Signs out on the server and forgets this device's session and cached data. */
export async function signOut() {
  await authClient.signOut();
  await saveSessionToken(null);
  api.clearCache();
  // The Help button starts big and solid again for the next person to sign in.
  requestHelpAttention();
}

/** Turns Better Auth error objects into a sentence people can act on. */
export function authErrorMessage(
  error: { message?: string; status?: number; code?: string } | null,
) {
  if (!error) return null;
  if (error.status === 429) return "Too many attempts. Wait a minute and try again.";
  if (error.code === "INVALID_EMAIL_OR_PASSWORD") return "That email and password don't match.";
  if (
    error.code === "USER_ALREADY_EXISTS" ||
    error.code === "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL"
  ) {
    return "An account with that email already exists. Try signing in.";
  }
  if (error.code === "PASSWORD_TOO_SHORT") return "Use at least 10 characters for your password.";
  return error.message ?? "Something went wrong. Please try again.";
}
